
import React, { useEffect, useMemo, useState } from 'react'
import { Col, FormGroup, FormLabel, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'

import { getItemsOptionsFromTaskList, getWorksOptionsFromTaskList } from './RelationsHelper'
import { deleteRelation, updateRelation } from '@/Services/ProgrammationManagerService'
import { removeRelationAction, updateRelationsAction } from '@/Redux/Actions/ProgrammationManagerActions'

import InputSelect from '../../components/InputSelect'
import InputText from '../../components/InputText'
import FormSavingStatus from '../../components/FormSavingStatus/FormSavingStatus'

import ToggleGroupIcon from '../../../AnimatedIcons/ToggleGroupIcon/ToggleGroupIcon'
import Spinner from '@/Components/SvgImages/Spinner/Spinner'
import TrashIcon from '@/Components/SvgImages/TrashIcon'

import './RelationItem.scss'

const RelationItem = (props: any) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened, setEditingItem, patchItemOpened } = useProgrammation()

  const { tasks } = useSelector((state: any) => state.ProgrammationReducer)
  const macroResult = useSelector((state: any) => state.MacroReducer.result)

  const [ isCollapsed, setIsCollapsed ] = useState(true)
  const [ fromTask, setFromTask ] = useState<any>()
  const [ toTask, setToTask ] = useState<any>()
  const [ type, setType ] = useState<any>()
  const [ saving, setSaving ] = useState(false)
  const [ deleting, setDeleting ] = useState(false)

  const relationsTypes = [
    {
      value: 'start_to_start',
      label: t('RELATIONS_CREATOR:Starts'),
    },
    {
      value: 'end_to_start',
      label: t('RELATIONS_CREATOR:Ends'),
    },
  ]

  const tasksOptions = useMemo(() => {

    if (itemOpened.type !== 'trabajo') {

      return getItemsOptionsFromTaskList({
        list: macroResult,
        currentItem: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentItem'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    } else if (itemOpened.type === 'trabajo') {

      return getWorksOptionsFromTaskList({
        list: macroResult,
        currentWork: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentWork'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    }
  }, [macroResult, itemOpened])

  const getRelationType = (type: string) => {

    const relationType = relationsTypes.find((t: any) => t.value === type)

    return relationType ? relationType.label : ''
  }

  const validateRelation = (relation: any) => {

    const isValid = true

    const taskFromIsCurrentItem = relation.from_task === itemOpened._id
    const taskToIsCurrentItem = relation.to_task === itemOpened._id

    if (!taskFromIsCurrentItem && !taskToIsCurrentItem) {

      Swal.fire({
        title: t('RELATION_ITEM:TaskNotSelectedTitle'),
        text: t('RELATION_ITEM:TaskNotSelectedContent'),
        icon: 'warning',
        confirmButtonText: t('RELATION_ITEM:OkButton'),
        width: '500px',
      })
      return false
    }

    return isValid
  }

  const handleSaveRelation = async (relation: any) => {

    if (!validateRelation(relation)) return

    setEditingItem(true)
    setSaving(true)

    const [ error, data ] = await updateRelation(props.item._id, relation)

    setSaving(false)
    setEditingItem(false)

    if (error) {
      return console.log(error)
    }

    patchItemOpened({
      relations: itemOpened.relations.map((r: any) => r._id === props.item._id ? relation : r)
    })

    dispatch(updateRelationsAction(data))
  }

  const handleChangeTaskFrom = (task_id: string) => {

    setFromTask(tasks.find((task: any) => task._id === task_id))

    handleSaveRelation({
      type,
      from_task: task_id,
      to_task: toTask._id,
    })
  }

  const handleChangeType = (type: string) => {

    setType(type)

    handleSaveRelation({
      type,
      from_task: fromTask._id,
      to_task: toTask._id,
    })
  }

  const handleChangeTaskTo = (task_id: string) => {

    setToTask(tasks.find((task: any) => task._id === task_id))

    handleSaveRelation({
      type,
      from_task: fromTask._id,
      to_task: task_id,
    })
  }

  const handleDelete = async () => {

    setEditingItem(true)
    setDeleting(true)

    const [ error, data ] = await deleteRelation(props.item._id)

    setDeleting(false)
    setEditingItem(false)

    if (error) {
      return console.log(error)
    }

    patchItemOpened({
      relations: itemOpened.relations.filter((r: any) => r._id !== props.item._id)
    })

    dispatch(removeRelationAction({ relation_id: props.item._id, ...data }))
  }

  useEffect(() => {

    const getTasks = () => {

      const fromTask = tasks.find((task: any) => task._id === props.item.from_task)
      const toTask = tasks.find((task: any) => task._id === props.item.to_task)

      setFromTask(fromTask)
      setToTask(toTask)
    }

    setType(props.item.type)

    getTasks()
  }, [ props.item ])

  if (!fromTask || !toTask) return null

  return (
    <div className="relation-item">
      <div className="relation-item-header">
        <div className="relation-item-name"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {t('RELATIONS_CREATOR:When')}{' '}
          <strong>{fromTask.name || t('RELATIONS_CREATOR:Nameless')}</strong>{' '}
          {getRelationType(type).toLowerCase()}{' '}
          {t('RELATIONS_CREATOR:Then').toLowerCase()}{' '}
          <strong>{toTask.name || t('RELATIONS_CREATOR:Nameless')}</strong>{' '}
          {t('RELATIONS_CREATOR:Starts').toLowerCase()}
        </div>
        <div className="relation-item-actions">
          <button
            className={`relation-item-button ${isCollapsed ? 'collapsed' : ''}`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ToggleGroupIcon
              size={20}
              isOpen={!isCollapsed}
            />
          </button>
        </div>
      </div>
      <div className={`relation-item-body ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="relation-item-body-content">
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
            {saving && (
              <FormSavingStatus saving={saving} />
            )}
            <button
              className="btn btn-danger"
              onClick={() => handleDelete()}
              disabled={props.readOnly || saving}
            >
              {deleting && (
                <>
                  <Spinner
                    height={20}
                    width={20}
                    fill="#e21c1c"
                  /> {t('RELATIONS_CREATOR:DeleteButton')}
                </>
              )}
              {!deleting && (
                <>
                  <TrashIcon
                    height={20}
                    width={20}
                    color="#e21c1c"
                  /> {t('RELATIONS_CREATOR:DeleteButton')}
                </>
              )}
            </button>
          </div>
          <FormGroup>
            <FormLabel>
              {t('RELATIONS_CREATOR:When')}
            </FormLabel>
            <Row>
              <Col xs={8} className='col-task-right'>
                <InputSelect
                  value={fromTask._id}
                  options={tasksOptions}
                  onChange={handleChangeTaskFrom}
                  renderOption={(option: any) => (
                    <>
                      {option.isGroup && (
                        <optgroup key={option.value} label={option.label}></optgroup>
                      )}
                      {!option.isGroup && (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      )}
                    </>
                  )}
                  disabled={saving}
                />
              </Col>
              <Col xs={4} className='col-task-left'>
                <InputSelect
                  value={type || 'start_to_start'}
                  options={relationsTypes}
                  onChange={handleChangeType}
                  renderOption={(option: any) => (
                    <>
                      {option.isGroup && (
                        <optgroup key={option.value} label={option.label}></optgroup>
                      )}
                      {!option.isGroup && (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      )}
                    </>
                  )}
                  disabled={saving}
                />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup>
            <FormLabel>
              {t('RELATIONS_CREATOR:Then')}
            </FormLabel>
            <Row>
              <Col xs={8} className='col-task-right'>
                <InputSelect
                  value={toTask._id}
                  options={tasksOptions}
                  onChange={handleChangeTaskTo}
                  renderOption={(option: any) => (
                    <>
                      {option.isGroup && (
                        <optgroup key={option.value} label={option.label}></optgroup>
                      )}
                      {!option.isGroup && (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      )}
                    </>
                  )}
                  disabled={saving}
                />
              </Col>
              <Col xs={4} className='col-task-left'>
                  <InputText
                    value={t('RELATIONS_CREATOR:Starts')}
                    disabled
                  />
              </Col>
            </Row>
          </FormGroup>
        </div>
      </div>
    </div>
  )
}

export default RelationItem
